import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {
  Plus, X, ArrowsClockwise, Tag, EnvelopeSimple, CaretRight,
} from '@phosphor-icons/react';
import toast from 'react-hot-toast';
import { EntityChip } from '../components/redesign/EntityChip';
import { Button } from '../components/redesign/ui';
import FlowsView from './Flows.view';

// ─── constants ────────────────────────────────────────────────────────────────

const ENTITIES = [
  { slug: 'abraaj-water',    label: 'Abraaj Water' },
  { slug: 'lofat-lifestyle', label: 'Lofat Lifestyle' },
  { slug: 'regency-kuwait',  label: 'Regency Kuwait' },
];

const RECIPIENT_TYPES = [
  { key: 'observer',       label: 'Observer',       desc: 'All ticket updates + read-only magic link',
    cls: 'bg-status-under-process/12 text-status-under-process' },
  { key: 'weekly_report',  label: 'Weekly Report',  desc: 'Weekly summary email',
    cls: 'bg-status-pending-finance/12 text-status-pending-finance' },
  { key: 'monthly_report', label: 'Monthly Report', desc: 'Monthly summary email',
    cls: 'bg-status-escalated/12 text-status-escalated' },
];

// Shared token-styled input class (mirrors other views).
const INPUT = 'w-full rounded-field border border-paper-border2 bg-paper px-3 py-2 text-sm text-ink outline-none focus:border-ink';

// ─── helpers ──────────────────────────────────────────────────────────────────

/**
 * Flatten all flows → recipients into a flat list, then group by email.
 * Returns: Map<email, { email, label, records: [{ id, flow_id, flow_display_name, entity_slug, recipient_type }] }>
 */
function buildGroupedRecipients(flows) {
  const map = new Map();
  for (const flow of flows) {
    const recipients = Array.isArray(flow.recipients) ? flow.recipients : [];
    for (const r of recipients) {
      if (!r || !r.email) continue;
      if (!map.has(r.email)) {
        map.set(r.email, { email: r.email, label: r.label || '', records: [] });
      }
      const group = map.get(r.email);
      if (r.label && !group.label) group.label = r.label;
      group.records.push({
        id:                r.id,
        flow_id:           flow.id,
        flow_display_name: flow.display_name || flow.complaint_type,
        entity_slug:       flow.entity_slug,
        recipient_type:    r.recipient_type,
      });
    }
  }
  return map;
}

// Flatten a group into the view's row shape.
function flattenRow(group) {
  const entities = [...new Set(group.records.map((r) => r.entity_slug))];
  const types    = [...new Set(group.records.map((r) => r.recipient_type))];
  const seen     = new Set();
  const flows    = [];
  for (const r of group.records) {
    if (seen.has(r.flow_id)) continue;
    seen.add(r.flow_id);
    flows.push({ id: r.flow_id, display_name: r.flow_display_name, entity_slug: r.entity_slug });
  }
  return {
    email: group.email,
    label: group.label,
    recordCount: group.records.length,
    entities,
    flows,
    types,
  };
}

// Type-summary tagline for the header subhead. Copy locked per approved spec:
//   "N observer[s] · N weekly · N monthly across N flow[s]"
// with zero-suppression on empty type counts, and a distinct empty-string
// fallback for the container to render "No recipients configured yet." on
// its own.
function computeTypeSummary(rows, flowCount) {
  if (!rows || rows.length === 0) return 'No recipients configured yet.';
  const counts = { observer: 0, weekly_report: 0, monthly_report: 0 };
  for (const row of rows) {
    for (const t of row.types) {
      if (counts[t] != null) counts[t] += 1;
    }
  }
  const segments = [];
  if (counts.observer > 0)       segments.push(`${counts.observer} observer${counts.observer > 1 ? 's' : ''}`);
  if (counts.weekly_report > 0)  segments.push(`${counts.weekly_report} weekly`);
  if (counts.monthly_report > 0) segments.push(`${counts.monthly_report} monthly`);
  if (segments.length === 0)     return 'No recipients configured yet.';
  const flowWord = flowCount === 1 ? 'flow' : 'flows';
  return `${segments.join(' · ')} across ${flowCount} ${flowWord}`;
}

// Coverage alert — entities missing an observer recipient. Returns null when
// coverage is complete or when there are no flows loaded yet.
function computeCoverageAlert(rows) {
  const covered = new Set();
  for (const row of rows) {
    if (!row.types.includes('observer')) continue;
    for (const slug of row.entities) covered.add(slug);
  }
  const missing = ENTITIES.filter((e) => !covered.has(e.slug));
  if (missing.length === 0) return null;
  const names = missing.map((e) => e.label);
  const list = names.join(', ');
  const message = missing.length === 1
    ? `No observer configured for ${names[0]}. Notifications for this entity's tickets won't reach anyone.`
    : `No observers configured for ${missing.length} entities: ${list}. Notifications for these entities' tickets won't reach anyone.`;
  return { message, entities: missing.map((e) => e.slug) };
}

// ─── SlidePanel ───────────────────────────────────────────────────────────────

function SlidePanel({ open, onClose, flows, editTarget, onSaved }) {
  const blankForm = {
    label:         editTarget?.label || '',
    email:         editTarget?.email || '',
    entities:      [],
    selectedFlows: [],
    types:         { observer: false, weekly_report: false, monthly_report: false },
  };

  const [form, setForm]     = useState(blankForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editTarget) {
      const entitySet = new Set(editTarget.records.map((r) => r.entity_slug));
      const flowSet   = new Set(editTarget.records.map((r) => r.flow_id));
      const typeSet   = new Set(editTarget.records.map((r) => r.recipient_type));
      setForm({
        label:         editTarget.label || '',
        email:         editTarget.email,
        entities:      [...entitySet],
        selectedFlows: [...flowSet],
        types: {
          observer:       typeSet.has('observer'),
          weekly_report:  typeSet.has('weekly_report'),
          monthly_report: typeSet.has('monthly_report'),
        },
      });
    } else {
      setForm(blankForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editTarget?.email]);

  const toggleEntity = (slug) => {
    setForm((prev) => {
      const next = prev.entities.includes(slug)
        ? prev.entities.filter((e) => e !== slug)
        : [...prev.entities, slug];
      const allowedFlowIds = flows.filter((f) => next.includes(f.entity_slug)).map((f) => f.id);
      return { ...prev, entities: next, selectedFlows: prev.selectedFlows.filter((id) => allowedFlowIds.includes(id)) };
    });
  };

  const toggleFlow = (flowId) => {
    setForm((prev) => ({
      ...prev,
      selectedFlows: prev.selectedFlows.includes(flowId)
        ? prev.selectedFlows.filter((id) => id !== flowId)
        : [...prev.selectedFlows, flowId],
    }));
  };

  const toggleType = (key) => {
    setForm((prev) => ({ ...prev, types: { ...prev.types, [key]: !prev.types[key] } }));
  };

  const visibleFlows = flows.filter((f) => form.entities.includes(f.entity_slug));
  const selectedTypes = Object.entries(form.types).filter(([, v]) => v).map(([k]) => k);

  const handleSave = async () => {
    if (!form.email.trim())               { toast.error('Email is required'); return; }
    if (form.selectedFlows.length === 0)  { toast.error('Select at least one flow'); return; }
    if (selectedTypes.length === 0)       { toast.error('Select at least one notification type'); return; }

    setSaving(true);
    try {
      // Edit = delete all existing records for this email, then recreate the
      // full (flow × type) fan-out. Matches the pre-redesign contract exactly.
      if (editTarget) {
        await Promise.all(
          editTarget.records.map((r) => api.delete(`/tickets/flows/recipients/${r.id}`).catch(() => {}))
        );
      }
      const calls = [];
      for (const flowId of form.selectedFlows) {
        for (const type of selectedTypes) {
          calls.push(api.post(`/tickets/flows/${flowId}/recipients`, {
            email:          form.email.trim(),
            label:          form.label.trim() || null,
            recipient_type: type,
          }));
        }
      }
      await Promise.all(calls);
      toast.success(editTarget ? 'Recipient updated' : 'Recipient added');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div className={`fixed right-0 top-0 z-50 flex h-full w-[480px] flex-col bg-paper-panel shadow-soft
                       transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-paper-border px-6 py-4">
          <h2 className="font-serif text-base text-ink">
            {editTarget ? 'Edit Recipient' : 'Add Recipient'}
          </h2>
          <button
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-field text-ink-muted hover:bg-paper-muted">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
          {/* Label */}
          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-xs text-ink-faint">
              <Tag size={12} className="text-ink-faint" />
              Label
            </label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
              placeholder="e.g. COO, Finance Team"
              className={INPUT}
              dir="auto"
            />
          </div>

          {/* Email */}
          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-xs text-ink-faint">
              <EnvelopeSimple size={12} className="text-ink-faint" />
              Email <span className="text-status-escalated">*</span>
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              placeholder="recipient@example.com"
              disabled={!!editTarget}
              className={`${INPUT} ${editTarget ? 'cursor-not-allowed bg-paper-muted text-ink-muted' : ''}`}
            />
            {editTarget && (
              <p className="mt-1 text-xs text-ink-faint">Email cannot be changed — remove and re-add to change email.</p>
            )}
          </div>

          {/* Entities */}
          <div>
            <label className="mb-2 block text-xs text-ink-faint">Entities</label>
            <div className="space-y-2">
              {ENTITIES.map((e) => (
                <label key={e.slug} className="flex cursor-pointer select-none items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={form.entities.includes(e.slug)}
                    onChange={() => toggleEntity(e.slug)}
                    className="h-4 w-4 rounded border-paper-border2 text-status-under-process focus:ring-status-under-process"
                  />
                  <EntityChip entitySlug={e.slug} />
                </label>
              ))}
            </div>
          </div>

          {/* Flows */}
          <div>
            <label className="mb-2 block text-xs text-ink-faint">
              Flows <span className="text-status-escalated">*</span>
            </label>
            {form.entities.length === 0 ? (
              <p className="text-xs italic text-ink-faint">Select at least one entity to see flows.</p>
            ) : visibleFlows.length === 0 ? (
              <p className="text-xs italic text-ink-faint">No flows available for selected entities.</p>
            ) : (
              <div className="space-y-3">
                {ENTITIES.filter((e) => form.entities.includes(e.slug)).map((entity) => {
                  const entityFlows = visibleFlows.filter((f) => f.entity_slug === entity.slug);
                  if (!entityFlows.length) return null;
                  return (
                    <div key={entity.slug}>
                      <div className="mb-1.5">
                        <EntityChip entitySlug={entity.slug} />
                      </div>
                      <div className="ml-1 space-y-1.5">
                        {entityFlows.map((flow) => (
                          <label key={flow.id} className="flex cursor-pointer select-none items-center gap-2.5">
                            <input
                              type="checkbox"
                              checked={form.selectedFlows.includes(flow.id)}
                              onChange={() => toggleFlow(flow.id)}
                              className="h-4 w-4 rounded border-paper-border2 text-status-under-process focus:ring-status-under-process"
                            />
                            <span className="text-sm text-ink">{flow.display_name || flow.complaint_type}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {form.selectedFlows.length > 0 && (
              <p className="mt-2 text-xs text-ink-faint">
                {form.selectedFlows.length} flow{form.selectedFlows.length > 1 ? 's' : ''} selected
              </p>
            )}
          </div>

          {/* Notification types */}
          <div>
            <label className="mb-2 block text-xs text-ink-faint">
              Notification Types <span className="text-status-escalated">*</span>
            </label>
            <div className="space-y-2">
              {RECIPIENT_TYPES.map((t) => (
                <label key={t.key} className="flex cursor-pointer select-none items-start gap-2.5">
                  <input
                    type="checkbox"
                    checked={form.types[t.key]}
                    onChange={() => toggleType(t.key)}
                    className="mt-0.5 h-4 w-4 rounded border-paper-border2 text-status-under-process focus:ring-status-under-process"
                  />
                  <div>
                    <span className={`inline-flex rounded-chip px-2 py-0.5 text-[11px] font-medium ${t.cls}`}>{t.label}</span>
                    <p className="mt-0.5 text-xs text-ink-faint">{t.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Summary preview */}
          {form.selectedFlows.length > 0 && selectedTypes.length > 0 && (
            <div className="flex items-start gap-2 rounded-field border border-status-under-process/30 bg-status-under-process/8 px-4 py-3 text-xs text-status-under-process">
              <CaretRight size={13} className="mt-0.5 flex-none" />
              <span>
                Will create <strong>{form.selectedFlows.length * selectedTypes.length}</strong> recipient
                record{form.selectedFlows.length * selectedTypes.length > 1 ? 's' : ''}
                ({form.selectedFlows.length} flow{form.selectedFlows.length > 1 ? 's' : ''} ×{' '}
                {selectedTypes.length} type{selectedTypes.length > 1 ? 's' : ''}).
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-paper-border px-6 py-4">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving && <ArrowsClockwise size={13} className="animate-spin" />}
            {saving ? 'Saving…' : editTarget ? 'Update' : 'Add Recipient'}
          </Button>
        </div>
      </div>
    </>
  );
}

// ─── main container ──────────────────────────────────────────────────────────

export default function Flows() {
  const { user } = useAuth();
  const [flowsData, setFlowsData]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [panelOpen, setPanelOpen]   = useState(false);
  const [editTarget, setEditTarget] = useState(null); // null = add, object = edit

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/tickets/flows');
      setFlowsData(res.data || []);
    } catch {
      toast.error('Failed to load flows');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Grouped rows (view-facing row shape).
  const groupedMap = buildGroupedRecipients(flowsData);
  const groups = [...groupedMap.values()];
  const rows = groups.map(flattenRow);

  const openAdd = () => { setEditTarget(null); setPanelOpen(true); };

  const openEdit = (email) => {
    const group = groupedMap.get(email);
    if (!group) return;
    setEditTarget(group);
    setPanelOpen(true);
  };

  const handleRemove = async (email) => {
    const group = groupedMap.get(email);
    if (!group) return;
    if (!window.confirm(`Remove all subscriptions for ${group.email}? This cannot be undone.`)) return;
    try {
      await Promise.all(group.records.map((r) => api.delete(`/tickets/flows/recipients/${r.id}`)));
      toast.success('Recipient removed');
      load();
    } catch {
      toast.error('Failed to remove recipient');
    }
  };

  const typeSummary   = computeTypeSummary(rows, flowsData.length);
  const coverageAlert = user?.role === 'super_admin' ? computeCoverageAlert(rows) : null;

  return (
    <>
      <FlowsView
        user={user}
        rows={rows}
        flows={flowsData}
        loading={loading}
        typeSummary={typeSummary}
        coverageAlert={coverageAlert}
        onRefresh={load}
        onAddRecipient={openAdd}
        onEditRecipient={openEdit}
        onRemoveRecipient={handleRemove}
      />
      <SlidePanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        flows={flowsData}
        editTarget={editTarget}
        onSaved={load}
      />
    </>
  );
}

